import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import 'intro.js/introjs.css';

import ResourceAllocation from '@/components/resource-allocation/resource-allocation';
import { ActionCenterV2 } from '@/components/action-center/action-center-v2';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { ROUTES, urls } from '@/urls';
import { getToken } from '@/lib/common';
import { hasUserAccessTo } from '@/lib/permissions';
import { AppCard } from '@/components/app-card/app-card';
import { AppContainer } from '@/components/ui';
import { StatCard, StatRow } from '@/components/ds';
import { PermissionArea } from '@cloudcost/shared-types/users';
import { ActionCenterItemV2 } from '@cloudcost/shared-types/action-center-v2';
import { useAllocation } from './hooks/useAllocation';
import AllocationCharts from './components/allocation-charts';
import AllocationFilters from './components/allocation-filters';
import { useActionItemDetails } from '@/hooks/useActionItemDetails';
import ActionItemTextDialog from '@/components/action-center/action-item-text-dialog';
import { useActionCenterViewController } from '@/hooks/useActionCenterViewController';
import { createAllocationActionCenterHandler } from '@/screens/allocation/action-center-view.config';
import { scrollSectionIntoView } from '@/utils/actionCenterFocus';

function Allocation() {
  const [filters, setFilters] = useState({
    cloudAccount: '',
    resourceOwner: '',
  });
  const [filterData, setFilterData] = useState({
    cloudAccounts: [] as any[],
    resourceOwners: [] as any[],
  });
  const [filterDataLoading, setFilterDataLoading] = useState(false);
  const [focusFilters, setFocusFilters] = useState<Record<string, string | undefined>>({});
  const [assignmentFilterMode, setAssignmentFilterMode] = useState<
    'allUnassigned' | 'missingOwner' | 'missingSystem' | 'all'
  >('allUnassigned');
  const [assignmentFilterCounts, setAssignmentFilterCounts] = useState({
    all: 0,
    allUnassigned: 0,
    missingOwner: 0,
    missingSystem: 0,
  });

  const {
    data: allocationData,
    loading: allocationLoading,
    user,
    refetch: refetchAllocationData,
  } = useAllocation();

  const [actionCenterItems, setActionCenterItems] = useState<ActionCenterItemV2[]>([]);
  const [selectedActionItemId, setSelectedActionItemId] = useState<string | null>(null);
  const { item: v2Item } = useActionItemDetails('Allocation', selectedActionItemId, actionCenterItems);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Extract unique resource owners from allocation data
  const resourceOwnersFromData = useMemo(() => {
    const ownerMap = new Map<number, { id: number; username: string; firstName: string }>();
    allocationData.resources?.forEach((resource) => {
      if (resource.owner_id && resource.owner_name) {
        if (!ownerMap.has(resource.owner_id)) {
          // Extract username from owner_name or use a default
          const parts = resource.owner_name.split(' ');
          ownerMap.set(resource.owner_id, {
            id: resource.owner_id,
            username: resource.owner_name, // We'll use the name as identifier
            firstName: parts[0] || resource.owner_name,
          });
        }
      }
    });
    return Array.from(ownerMap.values()).sort((a, b) =>
      a.firstName.localeCompare(b.firstName)
    );
  }, [allocationData.resources]);

  // Forecast per resource, from the already-fetched attestation-table rows.
  // Keyed by resource_id (the resource table id) — NOT `id`, which is the
  // resource_cost_monthly row id.
  const forecastByResourceId = useMemo(() => {
    const m = new Map<number, number>();
    (allocationData.resources || []).forEach((r) => {
      if (r.resource_id != null) m.set(r.resource_id, r.cost);
    });
    return m;
  }, [allocationData.resources]);

  // Derived top-row stats (styling only — no new fetches).
  // Uses the counts the screen already computes (assignmentFilterCounts,
  // populated by ResourceAllocation via onFilterCountsChange) plus the
  // resources already loaded by useAllocation.
  const allocationStats = useMemo(() => {
    const resources = allocationData.resources || [];

    // Total resources: prefer the screen's own "all" count; fall back to the
    // fetched resource list length until those counts are reported.
    const totalResources = assignmentFilterCounts.all || resources.length;

    // Allocated cost %: share of monthly cost on resources that already have
    // BOTH an owner and a system, versus total monthly cost.
    const totalCost = resources.reduce((sum, r) => sum + (r.cost || 0), 0);
    const allocatedCost = resources
      .filter((r) => r.owner_id != null && r.system_id != null)
      .reduce((sum, r) => sum + (r.cost || 0), 0);
    const allocatedPct =
      totalCost > 0 ? Math.round((allocatedCost / totalCost) * 100) : 0;

    return {
      totalResources,
      allocatedPct,
      unassigned: assignmentFilterCounts.allUnassigned,
      missingSystem: assignmentFilterCounts.missingSystem,
    };
  }, [allocationData.resources, assignmentFilterCounts]);


  // Fetch filter data
  useEffect(() => {
    setFilterDataLoading(true);
    Promise.all([
      fetchWrapper.get(urls.DevOps.getFilterCloudAccounts).catch((err) => {
        console.error('Error fetching cloud accounts:', err);
        return [];
      }),
    ])
      .then(([cloudAccounts]) => {
        setFilterData({
          cloudAccounts: cloudAccounts || [],
          resourceOwners: resourceOwnersFromData,
        });
      })
      .finally(() => {
        setFilterDataLoading(false);
      });
  }, [resourceOwnersFromData]);

  const cloudAccountExists = useCallback(
    (id: string) =>
      filterData.cloudAccounts.some((ca: any) => String(ca.id) === id),
    [filterData.cloudAccounts],
  );

  const resourceOwnerExists = useCallback(
    (id: string) =>
      filterData.resourceOwners.some((owner: any) => String(owner.id) === id),
    [filterData.resourceOwners],
  );

  useActionCenterViewController({
    module: 'Allocation',
    items: actionCenterItems,
    enabled: !filterDataLoading,
    handler: createAllocationActionCenterHandler({
      setSelectedItemId: setSelectedActionItemId,
      setIsDialogOpen,
      setFocusFilters,
      cloudAccountExists,
      resourceOwnerExists,
      setAssignmentFilterMode,
    }),
  });

  // Update filters when focusFilters change (from action item)
  useEffect(() => {
    if (Object.keys(focusFilters).length > 0) {
      setFilters(prev => ({
        ...prev,
        ...(focusFilters.cloudAccount !== undefined ? { cloudAccount: focusFilters.cloudAccount } : {}),
        ...(focusFilters.resourceOwner !== undefined ? { resourceOwner: focusFilters.resourceOwner } : {}),
      }));
    }
  }, [focusFilters]);

  // Scroll to allocation filters/charts when action item is clicked (only when item is selected, not when cleared)
  useEffect(() => {
    if (Object.keys(focusFilters).length > 0 && selectedActionItemId && isDialogOpen) {
      scrollSectionIntoView('[data-allocation-filters-section]', { delayMs: 600 });
    }
  }, [focusFilters, selectedActionItemId, isDialogOpen]);

  const handleFiltersChange = (newFilters: {
    cloudAccount: string;
    resourceOwner: string;
  }) => {
    setFilters(newFilters);
  };

  if (!getToken()) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return (
    <AppContainer>
      <StatRow columns={4}>
        <StatCard
          icon="allocation"
          tone="primary"
          value={allocationStats.totalResources}
          label="Total resources"
        />
        <StatCard
          icon="shield"
          tone="success"
          value={`${allocationStats.allocatedPct}%`}
          label="Allocated cost"
        />
        <StatCard
          icon="warn"
          tone="warn"
          value={allocationStats.unassigned}
          label="Unassigned"
        />
        <StatCard
          icon="warn"
          tone="danger"
          value={allocationStats.missingSystem}
          label="Missing system"
        />
      </StatRow>

      <ActionCenterV2
        endpointUrl={urls.DevOps.getAllocationActionCenterV2}
        onItemsLoaded={setActionCenterItems}
      />

      <ActionItemTextDialog
        item={v2Item}
        currentUserId={user?.id}
        open={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedActionItemId(null);
        }}
      />

      {hasUserAccessTo(PermissionArea.ALLOCATION_TAB) && (
        <ResourceAllocation
          onAssignmentChange={refetchAllocationData}
          filterMode={assignmentFilterMode}
          onFilterModeChange={setAssignmentFilterMode}
          onFilterCountsChange={setAssignmentFilterCounts}
          forecastByResourceId={forecastByResourceId}
        />
      )}

              <AppCard className="card100" title="Allocation Overview">
                <div data-allocation-filters-section>
                  <AllocationFilters
                    onFiltersChange={handleFiltersChange}
                    data={filterData}
                    loading={filterDataLoading}
                    initialCloudAccount={filters.cloudAccount || focusFilters.cloudAccount}
                    initialResourceOwner={filters.resourceOwner || focusFilters.resourceOwner}
                  />
                </div>

        <div data-allocation-charts-section>
          <AllocationCharts
            data={allocationData}
            loading={allocationLoading}
            currency={user?.default_currency || 'USD'}
            cloudAccountFilter={filters.cloudAccount}
            resourceOwnerFilter={filters.resourceOwner}
          />
        </div>
      </AppCard>
    </AppContainer>
  );
}

export default Allocation;
